import React, { useMemo, useState } from "react";
import { useRouter } from "next/router";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslations } from '../../locales/useTranslations';
import KontaktFormSelect from "./KontaktFormSelect";

const COUNTRY_PHONE_OPTIONS = [
  { value: 'DE', flag: '🇩🇪', de: 'Deutschland', en: 'Germany', dial: '+49' },
  { value: 'AT', flag: '🇦🇹', de: 'Österreich', en: 'Austria', dial: '+43' },
  { value: 'CH', flag: '🇨🇭', de: 'Schweiz', en: 'Switzerland', dial: '+41' },
  { value: 'FR', flag: '🇫🇷', de: 'Frankreich', en: 'France', dial: '+33' },
  { value: 'IT', flag: '🇮🇹', de: 'Italien', en: 'Italy', dial: '+39' },
  { value: 'ES', flag: '🇪🇸', de: 'Spanien', en: 'Spain', dial: '+34' },
  { value: 'NL', flag: '🇳🇱', de: 'Niederlande', en: 'Netherlands', dial: '+31' },
  { value: 'BE', flag: '🇧🇪', de: 'Belgien', en: 'Belgium', dial: '+32' },
  { value: 'LU', flag: '🇱🇺', de: 'Luxemburg', en: 'Luxembourg', dial: '+352' },
  { value: 'US', flag: '🇺🇸', de: 'USA', en: 'United States', dial: '+1' },
  { value: 'GB', flag: '🇬🇧', de: 'Großbritannien', en: 'United Kingdom', dial: '+44' },
];

const KontaktForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preferredContact, setPreferredContact] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('');
  const [msgSubject, setMsgSubject] = useState('');
  const { t } = useTranslations();
  const { locale } = useRouter();
  const isEn = locale === 'en';

  const countryOptions = useMemo(
    () =>
      COUNTRY_PHONE_OPTIONS.map((country) => ({
        value: country.value,
        label: `${country.flag} ${isEn ? country.en : country.de} (${country.dial})`,
      })),
    [isEn]
  );

  const preferredContactOptions = useMemo(
    () => [
      { value: 'email', label: t('contact_form_preferred_contact_email') },
      { value: 'phone', label: t('contact_form_preferred_contact_phone') },
      { value: 'both', label: t('contact_form_preferred_contact_both') },
    ],
    [t]
  );

  const subjectOptions = useMemo(
    () => [
      { value: 'Software-Entwicklung', label: t('contact_form_subject_software') },
      { value: 'Web-Entwicklung', label: t('contact_form_subject_web') },
      { value: 'App-Entwicklung', label: t('contact_form_subject_app') },
      { value: 'IT-Consulting', label: t('contact_form_subject_consulting') },
      { value: 'Business Analytics', label: t('contact_form_subject_analytics') },
      { value: 'Künstliche Intelligenz', label: t('contact_form_subject_ai') },
      { value: 'Automatisierung', label: t('contact_form_subject_automation') },
      { value: 'Digitale Transformation', label: t('contact_form_subject_digital') },
      { value: 'Support', label: t('contact_form_subject_support') },
      { value: 'Allgemeine Anfrage', label: t('contact_form_subject_general') },
    ],
    [t]
  );

  // Funktion zur Validierung der Kontakt-Präferenz
  function validateContactPreference(formData) {
    const email = formData.get('email');
    const phoneNumber = formData.get('phone_number');
    const phoneCountryCode = formData.get('phone_country_code');
    const contactPreference = formData.get('preferred_contact');

    // Wenn E-Mail als Kontaktmethode gewählt wurde, muss E-Mail vorhanden sein
    if (contactPreference === 'email' && !email) {
      toast.error(t('contact_form_email_required'), {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return false;
    }

    // Wenn Telefon als Kontaktmethode gewählt wurde, müssen Land und Telefonnummer vorhanden sein
    if (contactPreference === 'phone' && (!phoneCountryCode || !phoneNumber)) {
      toast.error(t('contact_form_phone_required'), {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return false;
    }

    // Wenn "beide" gewählt wurde, müssen sowohl E-Mail als auch Telefon-Daten vorhanden sein
    if (contactPreference === 'both' && (!email || !phoneCountryCode || !phoneNumber)) {
      toast.error(t('contact_form_both_required'), {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return false;
    }

    return true;
  }

  async function sendEmail(e) {
    e.preventDefault();

    // Prüfe, ob die Datenschutz-Checkbox aktiviert ist
    const privacyCheckbox = document.getElementById('privacy_consent');
    if (!privacyCheckbox.checked) {
      toast.error(t('contact_form_privacy_required'), {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    // Setze den Button auf deaktiviert, während die Anfrage verarbeitet wird
    setIsSubmitting(true);

    try {
      // Formulardaten sammeln
      const formData = new FormData(e.target);

      // Validiere Kontakt-Präferenz
      if (!validateContactPreference(formData)) {
        setIsSubmitting(false);
        return;
      }
      const data = {
        first_name: formData.get('first_name'),
        last_name: formData.get('last_name'),
        email: formData.get('email'),
        phone_number: formData.get('phone_number'),
        phone_country_code: formData.get('phone_country_code'),
        preferred_contact: formData.get('preferred_contact'),
        msg_subject: formData.get('msg_subject'),
        message: formData.get('message'),
        privacy_consent: formData.get('privacy_consent') === 'on'
      };

      // API-Aufruf an unser Backend
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8889';
      const response = await fetch(`${apiUrl}/api/contact-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      const result = await response.json();

      if (response.ok) {
        e.target.reset();
        setPreferredContact('');
        setPhoneCountryCode('');
        setMsgSubject('');
        toast.success(t('contact_form_success'), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } else {
        throw new Error(result.message || 'Unbekannter Fehler');
      }
    } catch (error) {
      console.error('Fehler beim Senden der Nachricht:', error);
      toast.error(error.message || t('contact_form_error'), {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setIsSubmitting(false); // Button wieder aktivieren
    }
  }

  return (
    <div>
      <ToastContainer />
      <div className="contact-form">
        <h3>{t('contact_form_title')}</h3>
        <form id="contactForm" onSubmit={sendEmail}>
          <div className="row">
            <div className="col-lg-6 col-md-6">
              <div className="form-group">
                <input
                  type="text"
                  name="first_name"
                  id="first_name"
                  className="form-control"
                  placeholder={t('first_name')}
                  required
                />
              </div>
            </div>

            <div className="col-lg-6 col-md-6">
              <div className="form-group">
                <input
                  type="text"
                  name="last_name"
                  id="last_name"
                  className="form-control"
                  placeholder={t('last_name')}
                  required
                />
              </div>
            </div>

            <div className="col-lg-12 col-md-12">
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  id="email"
                  className="form-control"
                  placeholder={t('email')}
                  required={preferredContact === 'email' || preferredContact === 'both'}
                />
              </div>
            </div>

            <div className="col-lg-6 col-md-6">
              <div className="form-group">
                <KontaktFormSelect
                  id="phone_country_code"
                  name="phone_country_code"
                  value={phoneCountryCode}
                  onChange={setPhoneCountryCode}
                  placeholder={t('contact_form_country_code_placeholder')}
                  options={countryOptions}
                  required={preferredContact === 'phone' || preferredContact === 'both'}
                />
              </div>
            </div>

            <div className="col-lg-6 col-md-6">
              <div className="form-group">
                <input
                  type="text"
                  name="phone_number"
                  id="phone_number"
                  className="form-control"
                  placeholder={t('phone')}
                  required={preferredContact === 'phone' || preferredContact === 'both'}
                />
              </div>
            </div>

            <div className="col-lg-6 col-md-6">
              <div className="form-group">
                <KontaktFormSelect
                  id="preferred_contact"
                  name="preferred_contact"
                  value={preferredContact}
                  onChange={setPreferredContact}
                  placeholder={t('contact_form_preferred_contact_placeholder')}
                  options={preferredContactOptions}
                  required
                />
              </div>
            </div>

            <div className="col-lg-6 col-md-6">
              <div className="form-group">
                <KontaktFormSelect
                  id="msg_subject"
                  name="msg_subject"
                  value={msgSubject}
                  onChange={setMsgSubject}
                  placeholder={t('contact_form_subject_placeholder')}
                  options={subjectOptions}
                  required
                />
              </div>
            </div>

            <div className="col-lg-12 col-md-12">
              <div className="form-group">
                <textarea
                  name="message"
                  className="form-control"
                  id="message"
                  rows="6"
                  placeholder={t('message')}
                ></textarea>
              </div>
            </div>

            <div className="col-lg-12 col-md-12">
              <div className="form-group">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="privacy_consent"
                    name="privacy_consent"
                    required
                  />
                  <label className="form-check-label" htmlFor="privacy_consent">
                    {t('contact_form_privacy_consent')}
                  </label>
                </div>
              </div>
            </div>

            <div className="col-lg-12 col-md-12 text-center">
              <button
                type="submit"
                className={`default-btn kontakt-form__submit-btn ${isSubmitting ? 'kontakt-form__submit-btn--submitting' : ''}`}
                disabled={isSubmitting}
              >
                <i className="bx bxs-paper-plane"></i>
                {t('contact_form_send')} <span></span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KontaktForm;
